import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppointmentResponse } from '../../api/types';
import { ErrorState } from '../../components/states/StateViews';
import { useLocalisedName } from '../../i18n/localisedName';
import { clinicDay, clinicTime, longDate } from './clinicTime';

/**
 * The bookings a calendar change would touch, and the choice, shown before anything changes.
 *
 * Two callers say different things about what going ahead does, so the explanation is theirs to
 * give: a branch closing a day or changing its hours cancels the bookings (CR-12), and the confirm
 * button is drawn as the destructive act it is; a doctor's new week leaves them booked and puts
 * them on a worklist (CR-20). Going back changes nothing in either case.
 */
export function AffectedBookings({
  affected,
  title,
  explanation,
  confirmLabel,
  destructive = true,
  pending,
  error,
  onConfirm,
  onBack,
}: {
  readonly affected: readonly AppointmentResponse[];
  readonly title: string;
  readonly explanation: string;
  readonly confirmLabel: string;
  readonly destructive?: boolean;
  readonly pending: boolean;
  readonly error: unknown;
  readonly onConfirm: () => void;
  readonly onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const headingId = useId();

  return (
    <div className="callout callout--warning affected-bookings" role="region" aria-labelledby={headingId}>
      <h3 className="affected-bookings__title" id={headingId}>
        {title}
      </h3>
      <p>{explanation}</p>

      <ul className="affected-bookings__list">
        {affected.map((appointment) => (
          <li key={appointment.id}>
            <span className="numeric">
              {appointment.startsAt ? longDate(clinicDay(appointment.startsAt), i18n.language) : ''}
              {' · '}
              {clinicTime(appointment.startsAt)}
            </span>
            {' — '}
            <strong>{appointment.patientName}</strong>
            {appointment.patientPhone ? (
              <>
                {' · '}
                <span dir="ltr">{appointment.patientPhone}</span>
              </>
            ) : null}
            {appointment.doctorName ? ` · ${appointment.doctorName}` : ''}
            {' · '}
            {localised.name({ nameEn: appointment.serviceNameEn, nameAr: appointment.serviceNameAr })}
          </li>
        ))}
      </ul>

      {error ? <ErrorState error={error} /> : null}

      <div className="toolbar" style={{ marginBlockEnd: 0 }}>
        <button
          type="button"
          className={destructive ? 'button button--danger' : 'button button--primary'}
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? t('branches.saving') : confirmLabel}
        </button>
        <button type="button" className="button button--ghost" disabled={pending} onClick={onBack}>
          {t('branches.affected.back')}
        </button>
      </div>
    </div>
  );
}
